import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useDeferredValue,
  useCallback,
  memo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import type { TFunction } from 'i18next';
import {
  AddRounded,
  ChevronRightRounded,
  DeleteOutline,
  EditOutlined,
  PersonAddAlt1Rounded,
  SearchRounded,
  PeopleAltRounded,
  SchoolRounded,
  VpnKeyRounded,
} from '@mui/icons-material';
import { usePermissions } from '../../hooks/use-permissions';
import { GroupAccessDialog } from './components/GroupAccessDialog';
import { collection, query, where, onSnapshot, type DocumentData } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { useTranslation } from 'react-i18next';
import { headerEmail, listName } from './person-header';
import { MissingTenantView } from '../../components/common/MissingTenantView';
import {
  INSTITUTIONAL_ROLE_TYPES,
  LIFECYCLE_TYPES,
  SYSTEM_TYPES,
  isInstitutional as isInstitutionalPerson,
  isTeachingStaff,
} from '@dosfilos/shared';
import {
  CreatePersonDialog,
  EditPersonDialog,
  DeletePersonDialog,
  type PersonData,
} from './components/PersonDialogs';

interface PersonRow extends PersonData {
  deletedAt?: unknown;
  institutionalRoles?: string[];
}

const TYPE_COLORS: Record<
  string,
  'primary' | 'success' | 'warning' | 'info' | 'default' | 'error'
> = {
  // ── Ciclo de vida académico ──────────────────────────────────────────────
  student: 'primary',
  alumni: 'info',
  'prospecto-académico': 'warning',
  externo: 'error',
  // ── Roles institucionales clásicos ───────────────────────────────────────
  faculty: 'success',
  docente: 'success',
  tutor: 'success',
  decano: 'success',
  academico: 'success',
  coordinador: 'success',
  capellan: 'success',
  staff: 'warning',
  contact: 'default',
  // ── Roles de sistema (TenantRole ⊆ PersonType) ───────────────────────────
  tenantAdmin: 'error',
  registrar: 'warning',
  reviewer: 'info',
  mentor: 'success',
  finance: 'success',
  donor: 'default',
};

/** Genera iniciales de nombre para el Avatar */
function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

/** Color de avatar determinístico basado en el nombre */
const AVATAR_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#14b8a6',
];
function getAvatarColor(name: string): string {
  let hash = 0;
  for (const ch of name) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? '#6366f1';
}

// Los grupos vienen de `@dosfilos/shared`, que es de donde los lee también el
// conteo del asistente. Estaban escritos acá y el asistente tenía su copia: con
// dos listas, agregar un cargo nuevo cambiaba un número y no el otro, y quien
// preguntara teniendo esta pantalla delante leería la diferencia como un error.

type TFn = TFunction<'sis'>;

/** Role/lifecycle/system chips for a person — shared by card + row layouts. */
const PersonChips = memo(function PersonChips({
  person,
  t,
  dense,
}: {
  person: PersonRow;
  t: TFn;
  dense?: boolean;
}) {
  const institutionalRoles = person.institutionalRoles?.length
    ? person.institutionalRoles
    : (person.types?.filter((type) =>
        (INSTITUTIONAL_ROLE_TYPES as readonly string[]).includes(type),
      ) ?? []);
  const chipSx = dense ? { height: 20, fontSize: '0.68rem' } : { fontSize: '0.7rem' };
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {person.types
        ?.filter((type) => (LIFECYCLE_TYPES as readonly string[]).includes(type))
        .map((type) => (
          <Chip
            key={type}
            label={t(`people.types.${type}`, { defaultValue: type })}
            size="small"
            color={TYPE_COLORS[type] ?? 'default'}
            variant="outlined"
            sx={chipSx}
          />
        ))}
      {institutionalRoles.map((role) => (
        <Chip
          key={`inst-${role}`}
          label={t(`people.types.${role}`, { defaultValue: role })}
          size="small"
          color="success"
          variant="filled"
          sx={chipSx}
        />
      ))}
      {person.types
        ?.filter((type) => (SYSTEM_TYPES as readonly string[]).includes(type))
        .map((type) => (
          <Chip
            key={`sys-${type}`}
            label={t(`people.types.${type}`, { defaultValue: type })}
            size="small"
            color={TYPE_COLORS[type] ?? 'default'}
            variant="outlined"
            sx={chipSx}
          />
        ))}
    </Stack>
  );
});

interface PersonItemProps {
  person: PersonRow;
  t: TFn;
  onOpen: (id: string) => void;
  onEdit: (p: PersonRow) => void;
  onDelete: (p: PersonRow) => void;
}

/** Mobile card layout (xs/sm). */
const PersonCardMobile = memo(function PersonCardMobile({
  person,
  t,
  onOpen,
  onEdit,
  onDelete,
}: PersonItemProps) {
  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(person.id ?? '')}
      sx={{
        p: 1.75,
        mb: 1.5,
        borderRadius: 3,
        cursor: 'pointer',
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.07)',
        transition: 'all 0.15s ease',
        '&:active': { background: 'rgba(99,102,241,0.08)', transform: 'scale(0.99)' },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Avatar
          sx={{
            width: 46,
            height: 46,
            bgcolor: getAvatarColor(`${person.firstName}${person.lastName}`),
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {getInitials(person.firstName, person.lastName)}
        </Avatar>
        <Box flex={1} minWidth={0}>
          <Typography fontWeight={700} noWrap>
            {listName(person.firstName, person.lastName)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', mb: 0.75 }}
          >
            {headerEmail(person.email) ?? t('academicRecord.noEmail')}
          </Typography>
          <PersonChips person={person} t={t} dense />
        </Box>
        <Stack direction="row" alignItems="center" gap={0} flexShrink={0}>
          <IconButton
            size="small"
            sx={{ color: 'action.active' }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(person);
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(person);
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
          <ChevronRightRounded sx={{ color: 'text.disabled', fontSize: 20, ml: 0.5 }} />
        </Stack>
      </Stack>
    </Paper>
  );
});

/** Desktop row layout (md+) — flex grid mirroring the old table columns. */
const PersonRowDesktop = memo(function PersonRowDesktop({
  person,
  t,
  onOpen,
  onEdit,
  onDelete,
}: PersonItemProps) {
  return (
    <Box
      onClick={() => onOpen(person.id ?? '')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.25,
        cursor: 'pointer',
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'background 0.15s ease',
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .row-actions': { opacity: 1 },
      }}
    >
      {/* Name column */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1.4, minWidth: 0 }}>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: getAvatarColor(`${person.firstName}${person.lastName}`),
            fontSize: 14,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {getInitials(person.firstName, person.lastName)}
        </Avatar>
        <Box minWidth={0}>
          <Typography fontWeight={600} noWrap>
            {listName(person.firstName, person.lastName)}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {headerEmail(person.email) ?? t('academicRecord.noEmail')}
          </Typography>
        </Box>
      </Stack>
      {/* Roles column */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <PersonChips person={person} t={t} />
      </Box>
      {/* Actions column */}
      <Stack
        className="row-actions"
        direction="row"
        justifyContent="flex-end"
        sx={{ width: 120, opacity: 0, transition: 'opacity 0.15s ease' }}
      >
        <Tooltip title={t('people.actions.edit')}>
          <IconButton
            size="small"
            sx={{ color: 'action.active' }}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(person);
            }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('people.actions.delete')}>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(person);
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
});

export function PeopleListPage() {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  // H10: dar acceso a un grupo. Administración (también profesores) y registro
  // (alumnos); el servidor lo exige igual.
  const [groupAccessOpen, setGroupAccessOpen] = useState(false);
  const { isAdmin, hasRole } = usePermissions();
  const canGroupAccess = isAdmin || hasRole('registrar');
  const [editingPerson, setEditingPerson] = useState<PersonRow | null>(null);
  const [deletingPerson, setDeletingPerson] = useState<PersonRow | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    if (!currentTenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const q = query(
      collection(db, `tenants/${currentTenantId}/people`),
      where('deletedAt', '==', null),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d: DocumentData) => ({
          id: d.id,
          ...(d.data() as Omit<PersonRow, 'id'>),
        }));
        rows.sort((a, b) =>
          `${a.lastName}${a.firstName}`.localeCompare(`${b.lastName}${b.firstName}`),
        );
        setPeople(rows);
        setLoading(false);
      },
      (err) => {
        console.error('[PeopleListPage] Firestore error:', err);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [currentTenantId]);

  // Cargos institucionales en ambos campos (backward + new field)
  const isInstitutional = useCallback((p: PersonRow) => isInstitutionalPerson(p), []);
  const isDocente = useCallback((p: PersonRow) => isTeachingStaff(p), []);

  // Counts recomputed only when the people set changes — not on every keystroke.
  const typeFilterOptions = useMemo(
    () => [
      { value: 'all', label: t('people.filters.all'), count: people.length },
      {
        value: 'student',
        label: t('people.filters.student'),
        count: people.filter((p) => p.types?.includes('student')).length,
      },
      {
        value: 'alumni',
        label: t('people.filters.alumni'),
        count: people.filter((p) => p.types?.includes('alumni')).length,
      },
      {
        value: 'institutional',
        label: t('people.filters.institutional'),
        count: people.filter(isInstitutional).length,
      },
      {
        value: 'docente',
        label: t('people.filters.docente'),
        count: people.filter(isDocente).length,
      },
      {
        value: 'externo',
        label: t('people.filters.externo'),
        count: people.filter((p) => p.types?.includes('externo')).length,
      },
    ],
    [people, isInstitutional, isDocente, t],
  );

  // Defer search so typing stays responsive — filtering runs at lower priority
  // while the input updates immediately.
  const deferredSearch = useDeferredValue(search);
  const filtered = useMemo(() => {
    const q = deferredSearch.toLowerCase();
    return people.filter((p) => {
      const matchesSearch =
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q);
      const matchesType =
        typeFilter === 'all'
          ? true
          : typeFilter === 'institutional'
            ? isInstitutional(p)
            : typeFilter === 'docente'
              ? isDocente(p)
              : (p.types || []).includes(typeFilter);
      return matchesSearch && matchesType;
    });
  }, [people, deferredSearch, typeFilter, isInstitutional, isDocente]);

  // Label para el empty state según filtro activo
  const activeFilterLabel =
    typeFilterOptions.find((o) => o.value === typeFilter)?.label ?? t('people.filters.all');

  // ── Virtualized listing — render only on-screen rows, scrolling against the
  // window so the page keeps its natural scroll (no nested scroll container).
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const listRef = useRef<HTMLDivElement>(null);
  // Distance from document top to the list — the window virtualizer needs it to
  // place absolutely-positioned rows. Recomputed after layout (ref is null on
  // first render) and whenever the header above the list can change height.
  const [listOffset, setListOffset] = useState(0);
  useLayoutEffect(() => {
    const top = listRef.current?.offsetTop ?? 0;
    setListOffset((prev) => (prev === top ? prev : top));
  }, [isDesktop, loading, filtered.length]);
  const virtualizer = useWindowVirtualizer({
    count: filtered.length,
    estimateSize: () => (isDesktop ? 64 : 92),
    overscan: 8,
    scrollMargin: listOffset,
  });

  const handleOpen = useCallback((id: string) => navigate(`/people/${id}`), [navigate]);
  const handleEdit = useCallback((p: PersonRow) => setEditingPerson(p), []);
  const handleDelete = useCallback((p: PersonRow) => setDeletingPerson(p), []);

  if (!currentTenantId) {
    return <MissingTenantView />;
  }

  // «N personas · N estudiantes · N docentes», con plurales de i18next.
  // (Sin hook: va después de los retornos anticipados.)
  const studentCount = people.filter((p) => p.types?.includes('student')).length;
  const facultyCount = people.filter(isDocente).length;
  const headerSummary = [
    t('people.header.people', { count: people.length }),
    ...(studentCount > 0 ? [t('people.header.students', { count: studentCount })] : []),
    ...(facultyCount > 0 ? [t('people.header.faculty', { count: facultyCount })] : []),
  ].join(' · ');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* ── Header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1.5}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {t('people.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {headerSummary}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title={t('people.header.originHelp')} placement="bottom" arrow>
            <Chip
              icon={<PersonAddAlt1Rounded />}
              label={t('people.header.originChip')}
              variant="outlined"
              sx={{ color: 'text.secondary', cursor: 'help', display: { xs: 'none', sm: 'flex' } }}
            />
          </Tooltip>
          {canGroupAccess && (
            <Button
              variant="outlined"
              startIcon={<VpnKeyRounded />}
              onClick={() => setGroupAccessOpen(true)}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                borderColor: 'divider',
                color: 'text.primary',
              }}
            >
              {t('people.groupAccess.open')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddRounded />}
            onClick={() => setCreateOpen(true)}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            {t('people.header.addStaff')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SchoolRounded />}
            onClick={() => navigate('/students/bulk-enroll')}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            }}
          >
            {t('people.header.enrollStudent')}
          </Button>
        </Stack>
      </Stack>

      {/* ── Search + Filters (sticky) ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          pb: 2,
          pt: 0.5,
        }}
      >
        <TextField
          placeholder={t('people.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Type Filter Chips — scrollable en mobile */}
        <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
          <Stack
            direction="row"
            gap={1}
            sx={{ flexWrap: { xs: 'nowrap', md: 'wrap' }, minWidth: 'max-content' }}
          >
            {typeFilterOptions.map((opt) => (
              <Chip
                key={opt.value}
                label={`${opt.label} (${opt.count})`}
                onClick={() => setTypeFilter(opt.value)}
                color={typeFilter === opt.value ? 'primary' : 'default'}
                variant={typeFilter === opt.value ? 'filled' : 'outlined'}
                sx={{ fontWeight: typeFilter === opt.value ? 700 : 400 }}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Listing (window-virtualized) ── */}
      {loading ? (
        <Stack alignItems="center" py={8}>
          <CircularProgress />
        </Stack>
      ) : filtered.length === 0 ? (
        /* ── Empty state por filtro ── */
        <Stack alignItems="center" justifyContent="center" py={10} spacing={2}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PeopleAltRounded sx={{ fontSize: 32, color: 'text.disabled' }} />
          </Box>
          <Typography variant="h6" fontWeight={600} color="text.secondary">
            {search
              ? t('people.empty.noResults', { query: search })
              : t('people.empty.noneRegistered', { label: activeFilterLabel.toLowerCase() })}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {search
              ? t('people.empty.tryOther')
              : typeFilter === 'all'
                ? t('people.empty.createFirst')
                : t('people.empty.changeFilter')}
          </Typography>
        </Stack>
      ) : (
        <Box>
          {/* Desktop column header (md+) */}
          {isDesktop && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1,
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.875rem',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ flex: 1.4 }}>{t('people.columns.name')}</Box>
              <Box sx={{ flex: 1 }}>{t('people.columns.roles')}</Box>
              <Box sx={{ width: 120, textAlign: 'right' }}>{t('people.columns.actions')}</Box>
            </Box>
          )}

          {/* Virtual window — only on-screen rows are mounted. */}
          <Box ref={listRef} sx={{ position: 'relative', height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((vItem) => {
              const person = filtered[vItem.index];
              if (!person) return null;
              return (
                <Box
                  key={person.id ?? vItem.index}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vItem.start - virtualizer.options.scrollMargin}px)`,
                  }}
                >
                  {isDesktop ? (
                    <PersonRowDesktop
                      person={person}
                      t={t}
                      onOpen={handleOpen}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ) : (
                    <PersonCardMobile
                      person={person}
                      t={t}
                      onOpen={handleOpen}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <GroupAccessDialog
        open={groupAccessOpen}
        onClose={() => setGroupAccessOpen(false)}
        canInviteFaculty={isAdmin}
      />
      <CreatePersonDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        tenantId={currentTenantId}
      />
      {editingPerson && (
        <EditPersonDialog
          person={editingPerson}
          onClose={() => setEditingPerson(null)}
          tenantId={currentTenantId}
        />
      )}
      {deletingPerson && (
        <DeletePersonDialog
          person={deletingPerson}
          onClose={() => setDeletingPerson(null)}
          tenantId={currentTenantId}
        />
      )}
    </Box>
  );
}
